// src/hooks/useCard.ts
import { useState, useCallback, useEffect, useRef, useMemo } from "react";
import { cardService } from "../services/card.service";
import { CardData, CardCustomization, UpdateCardDTO } from "../types/card.types";
import { useAuth } from "./useAuth";
import toast from "react-hot-toast";

interface UseCardState {
  card: CardData | null;
  loading: boolean;
  error: Error | null;
}

const CARD_ID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function useCard(cardIdOrLinkId?: string) {
  const [state, setState] = useState<UseCardState>({
    card: null,
    loading: !!cardIdOrLinkId,
    error: null,
  });

  const { user } = useAuth();
  const isMounted = useRef(true);
  const fetchedRef = useRef<string | null>(null);

  // Cleanup on unmount
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  const setLoading = useCallback((loading: boolean) => {
    if (isMounted.current) {
      setState((prev) => ({ ...prev, loading }));
    }
  }, []);

  const setCard = useCallback(
    (cardOrUpdater: CardData | null | ((prev: CardData | null) => CardData | null)) => {
      if (!isMounted.current) return;

      setState((prev) => {
        const newCard =
          typeof cardOrUpdater === "function" ? cardOrUpdater(prev.card) : cardOrUpdater;

        return {
          ...prev,
          card: newCard,
          loading: false,
          error: null,
        };
      });
    },
    []
  );

  const setError = useCallback((error: Error | null) => {
    if (isMounted.current) {
      setState({ card: null, loading: false, error });
    }
  }, []);

  const fetchCard = useCallback(
    async (id: string) => {
      if (fetchedRef.current === id) return;
      fetchedRef.current = id;

      try {
        setLoading(true);
        const data = await cardService.getCard(id);
        setCard(data);
      } catch (err) {
        setError(err as Error);
        toast.error("Failed to load card");
      }
    },
    [setLoading, setCard, setError]
  );

  const fetchCardByLinkId = useCallback(
    async (linkId: string) => {
      if (fetchedRef.current === linkId) return;
      fetchedRef.current = linkId;

      try {
        setLoading(true);
        const data = await cardService.getCardByLinkId(linkId);
        setCard(data);
      } catch (err) {
        setError(err as Error);
        toast.error("This link is invalid or has expired");
      }
    },
    [setLoading, setCard, setError]
  );

  const createCard = useCallback(
    async (
      customization: CardCustomization & {
        petals?: number;
        isEditable?: boolean;
      }
    ) => {
      if (!user?.id || !user?.email) {
        toast.error("Please sign in first");
        return null;
      }

      try {
        setLoading(true);

        // Use the senderName from customization if provided, otherwise fall back to user name
        const senderName = customization.senderName?.trim()
          ? customization.senderName
          : user.name || user.email?.split("@")[0] || "Anonymous";

        const newCard = await cardService.createCard({
          senderId: user.id,
          senderEmail: user.email,
          senderName, // Use the customized sender name
          customization,
        });

        setCard(newCard);
        toast.success("Experience created successfully!");
        return newCard;
      } catch (err) {
        setError(err as Error);
        toast.error(err instanceof Error ? err.message : "Failed to create card");
        return null;
      }
    },
    [user, setLoading, setCard, setError]
  );

  const updateCard = useCallback(
    async (id: string, updates: UpdateCardDTO) => {
      try {
        setLoading(true);

        // Simply pass the updates as-is - senderName is inside customization
        const updated = await cardService.updateCard(id, updates);
        setCard(updated);
        return updated;
      } catch (err) {
        setError(err as Error);
        toast.error("Failed to update card");
        throw err;
      }
    },
    [setLoading, setCard, setError]
  );

  const markAsReceived = useCallback(
    async (id: string) => {
      try {
        setLoading(true);
        await cardService.markAsReceived(id);
        setCard((prev) => (prev ? { ...prev, status: "received" } : null));
        toast.success("Thank you for accepting! ❤️");
      } catch (err) {
        setError(err as Error);
        toast.error("Failed to accept card");
        throw err;
      }
    },
    [setLoading, setCard, setError]
  );

  // Auto-fetch when ID changes
  useEffect(() => {
    if (!cardIdOrLinkId) {
      setCard(null);
      fetchedRef.current = null;
      return;
    }

    setCard(null);
    setError(null);
    setLoading(true);
    fetchedRef.current = null;

    const isUUID = CARD_ID_REGEX.test(cardIdOrLinkId);

    if (isUUID) {
      fetchCard(cardIdOrLinkId);
    } else {
      fetchCardByLinkId(cardIdOrLinkId);
    }
  }, [cardIdOrLinkId, fetchCard, fetchCardByLinkId, setCard, setError, setLoading]);

  const refreshCard = useCallback(() => {
    if (cardIdOrLinkId) {
      fetchedRef.current = null;
      const isUUID = CARD_ID_REGEX.test(cardIdOrLinkId);

      if (isUUID) {
        fetchCard(cardIdOrLinkId);
      } else {
        fetchCardByLinkId(cardIdOrLinkId);
      }
    }
  }, [cardIdOrLinkId, fetchCard, fetchCardByLinkId]);

  return useMemo(
    () => ({
      card: state.card,
      loading: state.loading,
      error: state.error,
      createCard,
      updateCard,
      markAsReceived,
      refreshCard,
    }),
    [state, createCard, updateCard, markAsReceived, refreshCard]
  );
}

export default useCard;
